import { type FC } from 'react';
import {
  UiGrid,
  UiHStack,
  UiIconInfo,
  UiIconPencilSimple,
  UiIconPlusCircle,
  UiSpinner,
  UiStack,
  uiStyles,
  UiText,
} from '@/lib/ui';
import BaseMessageBarError from '@/base/MessageBar/Error';
import CurrencyTag from '@/account/component/CurrencyTag';
import { type SubscriptionEditor } from '@/account/hook/useSubscriptionPlanEditor';
import { SubscriptionPackage } from '@/api/constant/subscription';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import PackagePack1 from './Package/Pack1';
import PackagePack2 from './Package/Pack2';
import AddonList from './AddonList';
import ServicePackEditor from './ServicePackEditor';
import dayjs from 'dayjs';
import AAAFields from '@/account/component/Subscription/AAAFields';

export type Mode = 'normal' | 'AAA';

export interface ManagementProps {
  subscriptionEditor: SubscriptionEditor
  mode?: Mode
}

const PlanEditor: FC<ManagementProps> = ({
  subscriptionEditor,
  mode = 'normal',
}) => {
  const {
    subscription,
    // isLoading,
    selectPackage,
    toggleAddon,
    updateServicePackHours,
    toggleServicePack,
  } = subscriptionEditor;

  if (!subscription) {
    return (
      <UiStack p={8}>
        <BaseMessageBarError>
          Failed to load the subscription editor.
        </BaseMessageBarError>
      </UiStack>
    );
  }

  return (
    <>
      <UiStack alignItems={'stretch'} py={8} spacing={8} pb={16}>
        {!!subscription.plan.from && (
          <UiStack px={8}>
            <UiHStack
              px={6}
              py={4}
              bgColor={'yellow.50'}
              borderRadius={uiStyles.borderRadius}
            >
              <UiIconInfo />
              <UiHStack spacing={1}>
                <UiText>
                  This plan is effective from
                </UiText>
                <UiText color={'green.500'} fontWeight={600}>{dayjs(subscription.plan.from * 1000).format('DD MMMM YY')}</UiText>
              </UiHStack>
            </UiHStack>
          </UiStack>
        )}
        <UiStack justifyContent={'flex-start'} alignItems={'flex-start'} px={8}>
          <CurrencyTag currency={'AUD'} />
        </UiStack>
        <UiStack
          alignItems={'stretch'}
          px={8}
          flexGrow={1}
          spacing={4}
        >
          <UiText variant={'h6'}>Choose a package</UiText>
          <UiGrid templateColumns={'1fr 1fr'} gap={{ base: 4, lg: 8 }}>
            <PackagePack1
              subscription={subscription}
              onSelect={() => { return selectPackage(SubscriptionPackage.Pack1); }}
            />
            <PackagePack2
              subscription={subscription}
              onSelect={() => { return selectPackage(SubscriptionPackage.Pack2); }}
            />
          </UiGrid>
        </UiStack>
        <UiStack px={8}>
          <BaseDividerHorizontal height={2} />
        </UiStack>
        <UiStack
          spacing={4}
          px={8}
        >
          <UiStack spacing={0}>
            <UiText variant={'h6'}>Service pack</UiText>
            {/* <UiText variant={'body2'}>The service pack is paid yearly</UiText> */}
          </UiStack>
          <UiStack
            borderRadius={uiStyles.borderRadius}
            spacing={8}
          >
            <ServicePackEditor
              subscription={subscription}
              onHoursUpdate={updateServicePackHours}
              onToggle={toggleServicePack}
            />
          </UiStack>
        </UiStack>
        <UiStack px={8}>
          <BaseDividerHorizontal height={2} />
        </UiStack>
        <UiStack
          px={8}
          spacing={4}
        >
          <UiStack spacing={0}>
            <UiText variant={'h6'}>Manage add-ons</UiText>
            <UiText variant={'body2'}>The following add-ons are paid every 3 months</UiText>
          </UiStack>
          <UiStack
            // borderWidth={'1px'}
            // borderColor={'gray.300'}
            // borderStyle={'solid'}
            spacing={8}
            // pt={4}
            // p={8}
            borderRadius={uiStyles.borderRadius}
          >
            <UiStack spacing={4}>
              {/* <UiStack spacing={0}> */}
              {/*  <UiText variant={'body1'} fontWeight={'bold'}>Choose other add-ons</UiText> */}
              {/*  <UiText variant={'body2'}>The following add-ons are paid every 3 months</UiText> */}
              {/* </UiStack> */}
              <UiStack
                // p={8}
                borderRadius={uiStyles.borderRadius}
                // borderWidth={'1px'}
                // borderColor={'gray.300'}
                // borderStyle={'solid'}
                spacing={8}
              >
                <AddonList
                  subscription={subscription}
                  toggleAddon={toggleAddon}
                />
              </UiStack>
            </UiStack>
          </UiStack>
        </UiStack>
        <UiStack px={8}>
          <BaseDividerHorizontal height={2} />
        </UiStack>
        {mode === 'AAA' && (
          <UiStack px={8}>
            <AAAFields
              subscription={subscription}
              subscriptionEditor={subscriptionEditor}
            />
          </UiStack>
        )}
      </UiStack>
    </>
  );
};

export default PlanEditor;
